import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { LoadingIcon } from '../../../../common/views/LoadingIcon';
import { CatalogItem, CatalogSong } from '../../../../../redux/api/fymAPI.types';
import { useMusic24Capabilities } from '../../../../music24/hooks/useMusic24Capabilities';
import { MAX_MASTER_MB } from '../../song/masterFile';
import { useMasterUpload } from '../../song/useMasterUpload';
import { MasterDropZone } from '../../song/MasterDropZone';
import { MasterProgress } from '../../song/MasterProgress';
import { PickedMasterFile } from '../../song/PickedMasterFile';
import { RightsConfirmRow } from '../../song/RightsConfirmRow';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { StepActions } from '../StepActions';
import { AudioUnlocks } from './AudioUnlocks';
import { audioPreviewNote } from './audioPreviewNote';
import { isAudioFile } from './audioFileSniff';
import { useIsTouchDevice } from '../useIsTouchDevice';
import { withBrandWord } from '../../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AudioStepProps {
  accountId: number;
  item: CatalogItem;
  song: CatalogSong | undefined;
  eyebrow: string;
  track: TrackSave;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}

/**
 * Step 5, optional (design catalog-add-song e1-e5): what the master unlocks, then the multipart
 * master upload of the song page (`useMasterUpload`: `POST .../master/uploads`, the part PUTs,
 * `.../complete`). The rights box shows only for the first master of the artist. The page goes on
 * while music24-media reads the song.
 */
const AudioStepComponent: React.FC<AudioStepProps> = ({
  accountId,
  item,
  song,
  eyebrow,
  track,
  onBack,
  onNext,
  onSkip,
}) => {
  const intl = useIntl();
  const isTouch = useIsTouchDevice();
  const capabilities = useMusic24Capabilities();
  const previewNote = audioPreviewNote(capabilities);
  const [file, setFile] = useState<File>();
  const [rightsChecked, setRightsChecked] = useState(false);
  // "Upload audio" looked live but did nothing until the rights box was ticked (QA imp-3). A press
  // now marks the box and says what to do.
  const [askRights, setAskRights] = useState(false);
  // A file whose first bytes are not audio (a text file named `.mp3`, QA brk-11).
  const [isNotAudio, setNotAudio] = useState(false);
  const { upload, cancel, canStop, progress, error, needsRights } = useMasterUpload({ accountId, itemUuid: item.uuid });
  const isUploading = progress !== null;
  const master = song?.master ?? null;
  const hasMaster = master !== null || item.song.hasMaster;
  const removeFile = useCallback(() => {
    setFile(undefined);
    setNotAudio(false);
  }, []);

  const pickFile = useCallback((picked: File) => {
    setAskRights(false);
    setNotAudio(false);
    isAudioFile(picked)
      .then((isAudio) => {
        if (isAudio) {
          setFile(picked);
        } else {
          setNotAudio(true);
        }
      })
      .catch(console.error);
  }, []);

  const confirmRights = useCallback((checked: boolean) => {
    setRightsChecked(checked);
    if (checked) {
      setAskRights(false);
    }
  }, []);

  const save = useCallback(() => {
    if (file === undefined) {
      return;
    }
    if (needsRights && !rightsChecked) {
      setAskRights(true);
      return;
    }
    track(upload(file, rightsChecked))
      .then((outcome) => {
        if (outcome === 'saved') {
          setFile(undefined);
        }
      })
      .catch(console.error);
  }, [file, needsRights, rightsChecked, track, upload]);

  let question = intl.formatMessage(addSongFlowMessages.audioQuestion);
  let sub = intl.formatMessage(addSongFlowMessages.audioSub);
  if (isUploading) {
    question = intl.formatMessage(addSongFlowMessages.audioUploadingQuestion, { title: item.title });
    sub = intl.formatMessage(addSongFlowMessages.audioUploadingSub);
  } else if (hasMaster && file === undefined) {
    question = intl.formatMessage(addSongFlowMessages.audioDoneQuestion);
    sub = intl.formatMessage(addSongFlowMessages.audioDoneSub);
  }

  const isPicked = file !== undefined && !isUploading;
  const main =
    isPicked || (!hasMaster && !isUploading)
      ? {
          label: intl.formatMessage(addSongFlowMessages.audioUpload),
          onClick: save,
          disabled: file === undefined,
          testID: 'add-song-audio-upload',
        }
      : {
          label: intl.formatMessage(addSongFlowMessages.next),
          onClick: onNext,
          disabled: isUploading,
        };

  return (
    <>
      <StepHeader eyebrow={eyebrow} question={question} sub={sub} />
      {isUploading && (
        <MasterProgress title={file?.name ?? ''} progress={progress} onStop={canStop ? cancel : undefined} />
      )}
      {isPicked && (
        <PickedMasterFile file={file} isWaitingForRights={needsRights && !rightsChecked} onRemove={removeFile} />
      )}
      {!isUploading && file === undefined && !hasMaster && (
        <MasterDropZone
          title={intl.formatMessage(
            isTouch ? addSongFlowMessages.audioDropTitleTouch : addSongFlowMessages.audioDropTitle
          )}
          hint={intl.formatMessage(
            isTouch ? addSongFlowMessages.audioDropHintTouch : addSongFlowMessages.audioDropHint,
            { max: MAX_MASTER_MB }
          )}
          onFile={pickFile}
        />
      )}
      {isNotAudio && (
        <div className={styles.alertError} role="alert" data-testid="add-song-audio-not-audio">
          {intl.formatMessage(addSongFlowMessages.audioNotAudio)}
        </div>
      )}
      {!isUploading && file === undefined && hasMaster && (
        <div className={styles.reading} data-testid="add-song-audio-done">
          {master?.status === 'processing' ? (
            <div className={styles.readingLine}>
              <LoadingIcon small />
              {intl.formatMessage(addSongFlowMessages.audioReading)}
            </div>
          ) : (
            <div className={styles.readingLine}>
              <FontAwesomeIcon icon={faCheck} className={styles.savedIcon} />
              {master?.name ?? intl.formatMessage(addSongFlowMessages.audioPlayReady)}
            </div>
          )}
          <span className={styles.hint}>{intl.formatMessage(addSongFlowMessages.audioReadingNote)}</span>
        </div>
      )}
      {needsRights && file !== undefined && (
        <div className={clsx(askRights && styles.rightsAsk)} data-testid="add-song-audio-rights">
          <RightsConfirmRow checked={rightsChecked} onChange={confirmRights} disabled={isUploading} />
          {askRights && (
            <span className={styles.error} role="alert">
              {intl.formatMessage(addSongFlowMessages.audioRightsFirst)}
            </span>
          )}
        </div>
      )}
      {error !== null && (
        <div className={styles.alertError} role="alert">
          {withBrandWord(error)}
        </div>
      )}
      <div>
        <div className={clsx(styles.label, styles.blockLabel)}>
          {hasMaster
            ? intl.formatMessage(addSongFlowMessages.audioUnlocked, { title: item.title })
            : intl.formatMessage(addSongFlowMessages.audioUnlocks)}
        </div>
        <AudioUnlocks canRenderFullSongVideos={capabilities.canRenderFullSongVideos} />
      </div>
      {previewNote !== null && (
        <div className={styles.previewLine} data-testid={`add-song-audio-preview-${previewNote}`}>
          <span className={styles.lockChip}>
            {intl.formatMessage(
              previewNote === 'trial' ? addSongFlowMessages.audioTrial : addSongFlowMessages.audioFree
            )}
          </span>
          <span>
            {withBrandWord(
              intl.formatMessage(
                previewNote === 'trial' ? addSongFlowMessages.audioTrialBody : addSongFlowMessages.audioFreeBody
              )
            )}
          </span>
        </div>
      )}
      <StepActions onBack={onBack} onSkip={hasMaster ? undefined : onSkip} disabled={isUploading} main={main} />
    </>
  );
};

export const AudioStep = TypedMemo(AudioStepComponent);
